import * as PIXI from 'pixi.js';
import { TweenMax, TimelineMax } from 'gsap/TweenMax';

/** 事件定义(不包括第三方类库的事件) */
const EVENT_NAME_MAP = {
  /** 加载进度条 */
  loadProgress: 'loadProgress',
  /** 加载完成 */
  loadDone: 'loadDone',
  /** 动画播放中类似音视频的进度 */
  progress: 'progress',

  touchStart: 'touchStart',
  touchMove: 'touchMove',
  touchEnd: 'touchEnd',

  // 时间轴相关事件
  timelineStart: 'timelineStart',
  timelineUpdate: 'timelineUpdate',
  timelineComplete: 'timelineComplete'
}

/**
 * 一镜到底功能.
 * @libs [ pixi.js 5、 gsap.TweenMax、gsap.TimelineMax ]
 */
class LongTake {
  constructor(options) {
    this.init(options);
  }

  init(options) {
    this.initData(options);
    this.initScene();
  }

  initData(options) {
    /** 库 */
    this.lib = [ PIXI, TweenMax, TimelineMax ];

    /** 默认配置 */
    this.defaultOptions = {
      container: document.body,
      width: document.documentElement.clientWidth || window.innerWidth || document.body.clientWidth,
      height: document.documentElement.clientHeight || window.innerHeight || document.body.clientHeight,
      resource: { sprites: [] }, // 需要加载的资源
      autoLoad: true, // 资源自动加载
      designWidth: 750, // 设计宽度
      vertical: true, // 竖屏
      enableScroll: true, // 开启滚动
      autoSize: true // 自适应宽高
    }
    /** 配置 */
    this.options = Object.assign({}, this.defaultOptions, options);
    /** 容器 */
    this.container = this.options.container;
    /** 宽度 */
    this.width = this.options.width;
    /** 高度 */
    this.height = this.options.height;
    /** 资源 */
    this.resource = this.options.resource;
    /** 自动加载 */
    this.autoLoad = this.options.autoLoad;
    /** 是否能滚动 */
    this.enableScroll = this.options.enableScroll;
    /** 场景集合 */
    this.scenes = {};
    /** 精灵集合 */
    this.sprites = {};
    /** 回调函数集合 */
    this.callbacks = { loadProgress: [] };
    /** 定时器集合 */
    this.aniIntervals = [];
    /** 事件名集合 */
    this.eventNameMap = EVENT_NAME_MAP;
  }

  setDevice (designWidth = 750) {
    const w = document.documentElement.clientWidth || window.innerWidth || document.body.clientWidth,
      h = document.documentElement.clientHeight || window.innerHeight || document.body.clientHeight;
    this.deviceMin = (w < h) ? w : h;
    this.deviceMax = (w > h) ? w : h;
    this.deviceScale = this.deviceMin / designWidth
    this.app.stage.scale.set(this.deviceScale, this.deviceScale);
    // 设置滚动高度.
    if (w < h) {   // 根据横屏竖屏效果旋转舞台
      this.app.stage.rotation = 1.5708;
      this.app.stage.pivot.set(0.5);
      this.app.stage.x = w;
      this.stagePosition = this.options.vertical ? 'x' : 'y'
    } else {
      this.stagePosition = this.options.vertical ? 'y' : 'x'
    }
  }


  /**
   * 获取dom
   */
  getView() {
    return this.app.view
  }

  // =====================================================================
  // 背景相关
  // =====================================================================
  /** 添加背景颜色 */
  addBg(color) {
    const bgColor = parseInt(color, 16);
    // 创建背景.
    this.bg = new PIXI.Graphics();
    this.bg.beginFill(bgColor);
    this.bg.drawRect(0, 0, this.app.screen.width, this.app.screen.height * 3);
    this.bg.endFill();
    this.bg.x = 0;
    this.bg.y = 0;
    this.app.stage.addChild(this.bg);
    return this
  }

  // =====================================================================
  // 加载资源
  // =====================================================================
  load() {
    // 加载资源
    const loader = PIXI.Loader.shared;
    const { progress } = this.eventNameMap;
    loader
      .add(this.resource.sprites)
      .on(progress, this.loadProgress.bind(this))
      .load(this.loadDone.bind(this));
  }

  /** 加载进度条. */
  loadProgress(loader) {
    const { loadProgress } = this.eventNameMap;
    this.trigger(loadProgress, loader.progress);
  }

  /** 加载完成. */
  loadDone() {
    // 派发资源加载完信息.
    const { loadDone } = this.eventNameMap;
    this.trigger(loadDone);
  }
  // =====================================================================
  // 场景相关.
  // =====================================================================
  /** 初始化场景 */
  initScene() {
    const { width, height, options } = this;
    // @see https://github.com/pixijs/pixi.js#basic-usage-example
    // @see http://pixijs.download/release/docs/PIXI.Application.html
    this.app = new PIXI.Application({
      resolution: 2,
      transparent: true,
      width: width,
      height: height
    });
    // 加入场景
    // this.container.appendChild(this.app.view);
    // 如果需要则自动开始加载
    if (this.autoLoad) this.load();
  }
  addScene(name, options = {}) {
    const { x = 0, y = 0 } = options
    let touchValue = 0
    this.scenes[name] = new PIXI.Container(options)
    this.scenes[name].x = x
    this.scenes[name].y = y
    this.scenes[name].interactive = true
    this.scenes[name].on('touchstart', e => {
      if (this.touchendEffectTween) {
          this.touchendEffectTween.stop()
      }
      this.touchstartPosition = {};
      this.touchstartPosition.x = -this.scenes[name].x + e.data.global.x - touchValue;
      this.touchstartPosition.y = -this.scenes[name].x + e.data.global.y - touchValue;
      this._touchStart.bind(this)
    }).on('touchmove', e => {
      this.touchmovePosition = {};
      this.touchmovePosition.x = e.data.global.x;
      this.touchmovePosition.y = e.data.global.y;
      if (this.stagePosition === 'y') {
        touchValue = e.data.global.y - this.touchstartPosition.y
      } else {
        touchValue = e.data.global.x - this.touchstartPosition.x
      }
      if (this.options.vertical) {
        this.scrollHeight = this.app.stage.height - this.height;
      } else {
        this.scrollHeight = this.app.stage.width - this.deviceMax;
      }
      if (touchValue >= 0) {
        touchValue = 0
      } else if (Math.abs(touchValue) > this.scenes[name].width - this.app.screen.height && this.stagePosition === 'y') {
        touchValue = -(this.scenes[name].width - this.app.screen.height)
      } else if (Math.abs(touchValue) > this.scenes[name].width - this.app.screen.width && this.stagePosition === 'x') {
        touchValue = -(this.scenes[name].width - this.app.screen.width)
      }
      if (-touchValue > this.scrollHeight) {
        touchValue = -this.scrollHeight
      }
      this.touchmove(touchValue)
      this._touchMove.bind(this)
    }).on('touchend', e => {
      this._touchEnd.bind(this)
    })
    this.app.stage.addChild(this.scenes[name])
    /** 屏幕适配 */
    this.options.autoSize && this.setDevice(this.options.designWidth)
    return this
  }

  // =====================================================================
  // 精灵相关.
  // =====================================================================
  /** 初始化精灵. */
  initSprites() {
    if (typeof this.options.sprites === 'object') {
      Object.keys(this.options.sprites).forEach(key => {
        // 创建
        const options = this.options.sprites[key];
        // deprecated — since 5.0.0
        // const sprite = new PIXI.Sprite(PIXI.loader.resources[options.key].texture);
        const spriteResources = PIXI.Loader.shared.resources[options.key].texture;
        const sprite = new PIXI.Sprite(spriteResources);
        // 设置属性
        this.setSize(sprite, options.size);
        this.setAnchor(sprite, options.anchor);
        this.setPosition(sprite, options.position);
        // 加入场景.
        this.app.stage.addChild(sprite);
        this.sprites[key] = sprite;
      });
    }
  }

  addSprite(sceneName, name, options) {
    const spriteResources = PIXI.Loader.shared.resources[name].texture
    const sprite = new PIXI.Sprite(spriteResources)
    this.setSize(sprite, options.size);
    this.setAnchor(sprite, options.anchor);
    this.setPosition(sprite, options.position);
    sprite.sceneName = sceneName
    this.sprites[name] = sprite;
    this.addEvent(name, options)
    this.addAnimation(name, options.animations)
    this.scenes[sceneName].addChild(this.sprites[name])
    return this
  }

  addAnimationSprite(sceneName, name, options) {
    const spriteResources = []; // 素材的陣列
    for (let i=0; i < 4; i++) {
        let texture = PIXI.Loader.shared.resources[`${name}${i}`].texture;
        spriteResources.push(texture); // 把素材放進素材陣列裡
    };
    const sprite = new PIXI.AnimatedSprite(spriteResources)
    this.setSize(sprite, options.size);
    this.setAnchor(sprite, options.anchor);
    this.setPosition(sprite, options.position);
    this.sprites[name] = sprite;
    this.addEvent(name, options)
    this.scenes[sceneName].addChild(this.sprites[name])
    return this
  }

  // =====================================================================
  // 文字相关
  // =====================================================================
  /** 文字初始化 */
  initTexts() {
    if (typeof this.options.texts === 'object') {
      Object.keys(this.options.texts).forEach(key => {
        // 创建.
        const textOptions = this.options.texts[key];
        this.addText(key, textOptions)
      })
    }
  }

  addText(name, textOptions) {
    const text = new PIXI.Text(textOptions.text, textOptions.options)
    this.setAnchor(text, textOptions.anchor)
    this.setPosition(text, textOptions.position)
    this.texts[name] = text
    this.addEvent(name, textOptions, 'text')
    this.app.stage.addChild(this.texts[name])
    return this
  }

  // =====================================================================
  // 通用属性设置.
  // =====================================================================
  /** 设置尺寸 */
  setSize(obj, size = {}) {
    size = Object.assign({}, { mode: 'auto', width: 1, height: 1 }, size)
    if (size.mode === 'widthFit') { // 按宽度适应.
      const scale = this.app.screen.width * size.width / obj.width + this.deviceScale;
      obj.scale.x = scale;
      obj.scale.y = scale;
    } else if (size.mode === 'heightFit') { // 按高度适应.
      const scale = this.app.screen.height * size.height / obj.height;
      obj.scale.x = scale;
      obj.scale.y = scale;
    } else { // 自定义.
      obj.width = size.width;
      obj.height = size.height;
    }
  }

  /**
   * 设置锚点(文本的原点).
   *
   * @param {object} obj
   * @param {string|object} anchor
   */
  setAnchor(obj, anchor = 'center') {
    if (typeof anchor === 'string') {
      anchor = this.positionMap(anchor);
    }
    obj.anchor.x = anchor.x;
    obj.anchor.y = anchor.y;
  }

  /**
   * 设置位置.
   *
   * @param {object} obj
   * @param {string|object} position
   */
  setPosition(obj, position = {}) {
    if (typeof position === 'string') {
      position = this.positionMap(position);
    }
    position = Object.assign({}, { mode: 'auto' }, position)
    if (position.mode === 'percent') {
      obj.position.x = position.x * this.app.screen.width;
      obj.position.y = position.y * this.app.screen.height;
    } else {
      obj.position.x = position.x;
      obj.position.y = position.y;
    }
  }

  /**
   * 位置转换.
   *
   * @param {string} type 类型.
   */
  positionMap(type) {
    const map = {
      top: { x: 0.5, y: 0 },
      right: { x: 1, y: 0.5 },
      bottom: { x: 0.5, y: 1 },
      left: { x: 0, y: 0.5 },
      topLeft: { x: 0, y: 0 },
      topRight: { x: 1, y: 0 },
      bottomLeft: { x: 0, y: 1 },
      bottomRight: { x: 1, y: 1 },
      center: { x: 0.5, y: 0.5 }
    }
    return map[type] || { x: 0, y: 0 }
  }

  // =====================================================================
  // 进度相关.
  // =====================================================================
  initTimeLine() {
    /** 是否自动播放 */
    const paused = true;
    // @see https://www.tweenmax.com.cn/api/timelinemax/onUpdate
    // TimelineMax -- 创建时间轴
    // TimeMax -- 创建动画，然后将动画添加到时间轴中.
    this.timeline = new TimelineMax({
      paused: paused,
      onStart: this.timelineStart.bind(this),
      onUpdate: this.timelineUpdate.bind(this),
      onComplete: this.timelineComplete.bind(this)
    });
    return this
  }
  /**
   *
   * @param {*} targetName
   * @param {*} animations
   * @param {*} type sprite or text
   */
  addAnimation(targetName, animations, type = "sprite") {
    type = `${type}s`
    const obj = this[type] ? this[type][targetName] : null
    if (obj && animations && animations instanceof Array) {
      // 回调函数中的参数与 spriteAnimations textsAnimations 字段对应.
      animations.forEach(
        ({
          from,
          to,
          frames,
          infinite,
          frameRate,
          delay = 0,
          duration = 1
        }) => {
          const loader = PIXI.Loader.shared
          if (frames) {
            // 帧动画
            if (infinite) {
              // 循环
              obj.frames = frames
              obj.currentFrame = 0
              this.aniIntervals.push(
                setInterval(() => {
                  obj.currentFrame += 1
                  if (obj.currentFrame >= obj.frames.length)
                    obj.currentFrame = 0
                  const frame = obj.frames[obj.currentFrame]
                  obj.texture = loader.resources[frame].texture
                }, (duration * 1000) / frameRate)
              )
            } else {
              // 不循环
              const { progress } = this.eventNameMap
              this.on(progress, progress => {
                const frameProgress = (progress - delay) / duration
                let index = Math.floor(frameProgress * frames.length)
                if (index < frames.length && index >= 0) {
                  const frame = frames[index]
                  obj.texture = loader.resources[frame].texture
                }
              })
            }
          } else if (from || to) {
            // 过渡动画
            let action
            if (from && to) {
              action = TweenMax.fromTo(obj, duration, from, to)
            } else if (to) {
              action = TweenMax.to(obj, duration, to)
            } else if (from) {
              action = TweenMax.from(obj, duration, from)
            }
            const timeline = new TimelineMax({ delay })
            timeline.add(action, 0)
            timeline.play()
            if (!obj.animations) obj.animations = []
            obj.animations.push(timeline)
            if (!(to && to.repeat === -1)) {
              this.timeline.add(timeline, 0)
            }
          }
        }
      )
    }
    return this
  }

  /** 事件轴开始播放 */
  timelineStart() {
    const { timelineStart } = this.eventNameMap;
    this.trigger(timelineStart);
  }

  /** 事件轴播放完成 */
  timelineComplete() {
    const { timelineComplete } = this.eventNameMap;
    this.trigger(timelineComplete);
  }

  /** 时间轴正在播放 */
  timelineUpdate() {
    const { timelineUpdate } = this.eventNameMap;
    this.trigger(timelineUpdate);
  }

  // =====================================================================
  // 滑动相关.
  // =====================================================================
  /** 滑动初始化 */
  initTouch(options = {}) {
    if (this.alloyTouch) {
      return this
    }
    if (this.options.vertical) {
      this.scrollHeight = this.app.stage.height - this.height;
    } else {
      this.scrollHeight = this.app.stage.width - this.deviceMax;
    }
    // @see https://github.com/AlloyTeam/PhyTouch#usage
    // @see http://www.alloyteam.com/2016/12/mobile-web-touch-and-motion-solutions-alloytouch-open-source/
    const defaultOptions = {touch: 'body' }
    const addOptions = {
      min: -this.scrollHeight,
      vertical: this.stagePosition === 'y',
      max: 0,
      change: this.touchmove.bind(this),
      touchStart: this._touchStart.bind(this),
      touchMove: this._touchMove.bind(this),
      touchEnd: this._touchEnd.bind(this)
    }
    // const touchOptions = Object.assign(defaultOptions, this.options.touchOptions, addOptions, options);
    // this.alloyTouch = new PhyTouch(touchOptions);
    return this
  }

  _touchStart (event, value) {
    this._isTouchmove = false
    const { touchStart } = this.eventNameMap;
    this.trigger(touchStart, { event, value });
  }

  _touchMove (event, value) {
    this._isTouchmove = true
    const { touchMove } = this.eventNameMap;
    this.trigger(touchMove, { event, value });
  }

  _touchEnd (event, value) {
    this._isTouchmove = false
    const { touchEnd } = this.eventNameMap;
    this.trigger(touchEnd, { event, value });
  }

  /** 滑动处理 */
  touchmove(value) {
    // 播放总进度
    this.progress = -value / this.scrollHeight;
    this.progress = this.progress < 0 ? 0 : this.progress;
    this.progress = this.progress > 1 ? 1 : this.progress;
    if (this.enableScroll) {
      this.goToPosition(value)
    }
    // 触发事件
    const { progress } = this.eventNameMap;
    this.trigger(progress, { progress: this.progress, TweenMax, TimelineMax, PIXI, value });
  }

  /**
   * 镜头移动
   * @param {*} value 位置
   */
  goToPosition (value) {
    this.progress = -value / this.scrollHeight;
    this.progress = this.progress < 0 ? 0 : this.progress;
    this.progress = this.progress > 1 ? 1 : this.progress;
    this.app.stage.position[this.stagePosition || 'y'] = value;
    // 控制进度条
    this.timeline.seek(this.progress);
  }

  /** 销毁(周期) */
  destroy() {

  }

  addEvent(targetName, options, type = "sprite") {
    type = `${type}s`
    const obj = this[type] ? this[type][targetName] : null
    if (options.link) {
      obj.buttonMode = true
      obj.interactive = true
      const onHandleTap = e => {
        if (this._isTouchmove) {
          return
        }
        location.href = options.link
      }
      obj.on("tap", onHandleTap)
    }
    if (options.touch) {
      obj.buttonMode = true
      obj.interactive = true
      const eventName = options.touch.eventName || "eventName"
      const self = this
      obj.on("tap", e => {
        if (this._isTouchmove) {
          return
        }
        const params = { target: obj, TweenMax, TimelineMax, PIXI, event: e }
        // 事件有附加参数
        if (options.touch.params) {
          self.trigger(eventName, { ...options.touch.params, ...params })
        } else {
          self.trigger(eventName, params)
        }
      })
    }
    return this
  }

  // =====================================================================
  // 事件
  // =====================================================================
  /**
   * 监听事件.
   * @param {string} name 事件名.
   * @param {object} callback
   */
  on(name, callback) {
    this.callbacks[name] = this.callbacks[name] || []
    this.callbacks[name].push(callback);
    return this
  }

  /**
   * 移除事件监听.
   *
   * @param {*} name
   * @param {*} callback
   */
  off(name, callback) {
    const callbacks = this.callbacks[name];
    if ( callbacks && callbacks instanceof Array ) {
      const index = callbacks.indexOf(callback);
      // 有对应的事件则移除.
      if (index !== -1) callbacks.splice(index, 1);
    }
    return this
  }

  /**
   * 触发事件.
   *
   * @param {string} name 事件名.
   * @param {any}    params  传递给事件的参数
   */
  trigger(name, params) {
    const callbacks = this.callbacks[name];
    if ( callbacks && callbacks instanceof Array ) {
      // 执行回调.
      callbacks.forEach(cb => {
        cb(params);
      })
    }
  }
}

export default LongTake;
